import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from './basics.js';

/**
 * Catches a render error below it so one broken section never blanks the whole
 * page. The fallback is a warning Notice with the error name, a Reload button
 * and, when given, the caller's own recovery (for example closing a drawer).
 * React only offers this through a class component.
 */
export class ErrorBoundary extends Component<
  {
    children: ReactNode;
    /** Where the error happened, in the reader's words ("Version history"). */
    label?: string;
    /** Called once when an error is caught, after it was logged. */
    onError?: (error: Error) => void;
    /** Replaces the default fallback. */
    fallback?: (error: Error, reset: () => void) => ReactNode;
  },
  { error: Error | null }
> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The stack is what a bug report needs; no user data lives in a render error.
    // eslint-disable-next-line no-console
    console.error('Render error', error, info.componentStack);
    this.props.onError?.(error);
  }

  reset = (): void => this.setState({ error: null });

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(error, this.reset);
    return (
      <div className="warn stack-8" role="alert">
        <b>
          {this.props.label
            ? `${this.props.label} could not be shown.`
            : 'This could not be shown.'}
        </b>
        <span>
          {error.name}
          {error.message ? `: ${error.message}` : ''}
        </span>
        <div className="row nowrap">
          <Button small onClick={this.reset}>
            Try again
          </Button>
          <Button small onClick={() => window.location.reload()}>
            Reload the page
          </Button>
        </div>
      </div>
    );
  }
}
